import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Icon from '../../components/AppIcon';
import NotificationBell from '../../components/ui/NotificationBell';
import { logout, selectCurrentUser } from '../../features/auth/authSlice';
import { isProfessionalRole } from '../../lib/authPortals';

const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [
      { to: '/user-dashboard', label: 'Dashboard', icon: 'LayoutDashboard', exact: true },
      { to: '/notifications', label: 'Notifications', icon: 'Bell' },
      { to: '/saved-properties', label: 'Saved Properties', icon: 'Heart' },
      { to: '/notice-board', label: 'Notice Board', icon: 'ClipboardList' },
    ],
  },
  {
    label: 'Discover',
    items: [
      { to: '/property-search-browse', label: 'Browse Properties', icon: 'Search' },
      { to: '/workspace/bookings', label: 'Advisory Bookings', icon: 'Calendar' },
      { to: '/hostels', label: 'Hostels', icon: 'Building' },
      { to: '/hotels', label: 'Hotels', icon: 'BedDouble' },
    ],
  },
  {
    label: 'Account',
    items: [
      { to: '/user-dashboard/profile', label: 'My Profile', icon: 'User' },
    ],
  },
];

const UserLayout = ({ children, title, subtitle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center space-y-3 px-4">
          <Icon name="Lock" size={40} className="mx-auto text-slate-400" />
          <p className="text-slate-700 font-medium">Please sign in to access your dashboard.</p>
          <button
            type="button"
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-semibold"
            onClick={() => navigate('/login')}
          >
            Sign in
          </button>
        </div>
      </div>
    );
  }

  const Sidebar = ({ onNavigate }) => (
    <div className="flex flex-col h-full">
      <div className="px-5 py-6 border-b border-slate-800">
        <Link to="/" className="flex items-center gap-2 mb-4" onClick={onNavigate}>
          <img src="/assets/images/homelogo.png" alt="HireBroker" className="h-8" />
        </Link>
        <p className="text-[10px] uppercase tracking-[0.2em] text-sky-400/90 font-semibold">
          My dashboard
        </p>
        <p className="text-white font-semibold mt-1 truncate">{user?.name || 'User'}</p>
        <p className="text-xs text-slate-400 mt-0.5 truncate">
          {user?.email || user?.mobile || 'Property seeker'}
        </p>
        {isProfessionalRole(user?.account_role) && (
          <Link
            to="/agent-dashboard"
            onClick={onNavigate}
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-amber-400 hover:text-amber-300"
          >
            <Icon name="Briefcase" size={12} />
            Switch to Agent console
          </Link>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {NAV_GROUPS.map((group) => (
          <div key={group.label}>
            <p className="px-3 mb-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              {group.label}
            </p>
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const active = item.exact
                  ? location.pathname === item.to
                  : location.pathname === item.to ||
                    (item.to !== '/user-dashboard' && location.pathname.startsWith(item.to));
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      onClick={onNavigate}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                        active
                          ? 'bg-sky-500/15 text-sky-300 font-medium'
                          : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                      }`}
                    >
                      <Icon name={item.icon} size={18} />
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="p-4 border-t border-slate-800 space-y-2">
        <Link
          to="/"
          onClick={onNavigate}
          className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white"
        >
          <Icon name="Home" size={16} />
          Back to marketplace
        </Link>
        <button
          type="button"
          onClick={() => {
            dispatch(logout());
            navigate('/login');
          }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-red-400"
        >
          <Icon name="LogOut" size={16} />
          Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100 flex">
      <aside className="hidden lg:flex w-72 flex-col bg-slate-950 text-white fixed inset-y-0 left-0 z-30">
        <Sidebar />
      </aside>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-slate-950 text-white shadow-xl">
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:pl-72 min-h-screen flex flex-col">
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-200 px-4 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              className="lg:hidden p-2 rounded-lg border border-slate-200"
              onClick={() => setMobileOpen(true)}
            >
              <Icon name="Menu" size={20} />
            </button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-semibold text-slate-900 truncate">{title}</h1>
              {subtitle && (
                <p className="text-xs sm:text-sm text-slate-500 truncate">{subtitle}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell accent="sky" inboxPath="/notifications" />
            <Link
              to="/property-search-browse"
              className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
            >
              <Icon name="Search" size={16} />
              Find property
            </Link>
            <Link
              to="/notice-board"
              className="hidden md:inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50"
            >
              <Icon name="Plus" size={16} />
              Property request
            </Link>
            <Link
              to="/user-dashboard/profile"
              className="w-9 h-9 rounded-full bg-sky-100 text-sky-800 flex items-center justify-center text-sm font-bold hover:ring-2 hover:ring-sky-300"
              title="My profile"
            >
              {(user?.name || 'U').charAt(0).toUpperCase()}
            </Link>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-8">{children}</main>
      </div>
    </div>
  );
};

export default UserLayout;
